import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    FormControl,
    FormLabel,
    TextField,
    Button,
    Select,
    MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { sendApiRequest } from '../lib/api/public';
import notify from '../lib/notify';

type Props = {
    onClose: () => void;
    onSuccess: () => void;
    open: boolean;
    customQuestion?;
};

const CustomQuestionForm = ({ open, onClose, onSuccess, customQuestion }: Props) => {
    const [name, setName] = useState(customQuestion ? customQuestion.name : '');
    const [saving, setSaving] = useState(false);
    const [questions, setQuestions] = useState([]);

    const generateNewQuestion = () => ({ _id: Math.random(), createdAt: new Date(), type: 'input' });

    useEffect(() => {
        const questions = customQuestion ? customQuestion.questions || [] : [];

        if (questions.length === 0) {
            questions.push(generateNewQuestion());
        }

        setQuestions(questions.map(q => ({ ...q, options: (q.options || '').split('\n') })));
    }, []);

    const onSave = async () => {
        setSaving(true);

        try {
            await sendApiRequest({
                path: '/custom-questions/save',
                method: 'POST',
                data: {
                    _id: customQuestion ? customQuestion._id : null,
                    name,
                    questions: questions.map(q => ({ ...q, options: (q.options || []).join('\n') }))
                }
            });

            setSaving(false);

            notify('Success');

            onSuccess();
        } catch (error) {
            setSaving(false);
            notify(error);
        }
    }

    const onDelete = async () => {
        setSaving(true);

        try {
            await sendApiRequest({
                path: '/custom-questions/delete',
                method: 'POST',
                data: {
                    _id: customQuestion._id,
                }
            });

            onSuccess();

            setSaving(false);
        } catch (error) {
            setSaving(false);
            notify(error);
        }
    };

    const addQuestion = () => {
        questions.push(generateNewQuestion());
        setQuestions([...questions]);
    }

    const onDeleteQuestion = (question) => {
        const newQuestions = questions.filter(q => q._id !== question._id);
        setQuestions([...newQuestions]);
    }

    const onChangeType = (question, e) => {
        question.type = e.target.value;
        setQuestions([...questions]);
    }

    const onChangeTitle = (question, e) => {
        question.title = e.target.value;
        setQuestions([...questions]);
    }

    const addOption = (question) => {
        if (!question.options) {
            question.options = [];
        }

        question.options.push('');
        setQuestions([...questions]);
    }

    const onChangeOption = (question, index, value) => {
        question.options[index] = value;
        setQuestions([...questions]);
    }

    const onDeleteOption = (question, index) => {
        question.options.splice(index, 1);
        setQuestions([...questions]);
    }

    const renderQuestion = (question, index) => {
        let icon = null;
        const iconStyle = { color: '#969799', marginTop: '5px', marginRight: '5px' };

        if (question.type === 'checkbox') {
            icon = <CheckBoxOutlineBlankIcon style={iconStyle} />;
        } else if (question.type === 'multichoices') {
            icon = <RadioButtonUncheckedIcon style={iconStyle} />;
        }

        return (
            <div key={index} className='settings-question'>
                <CloseIcon onClick={onDeleteQuestion.bind(this, question)} className='delete-icon' />

                <FormControl fullWidth variant="outlined" required>
                    <TextField
                        inputProps={{
                            className: 'title'
                        }}
                        onChange={onChangeTitle.bind(this, question)}
                        value={question.title || ''}
                        placeholder={question.title ? null : 'Type your question ...'}
                    />
                </FormControl>

                <br /><br />

                <FormControl fullWidth>
                    <Select onChange={onChangeType.bind(this, question)} style={{ height: '40px' }} value={question.type || 'input'}>
                        <MenuItem value="input">Text (Single line)</MenuItem>
                        <MenuItem value="textarea">Text (Paragraph)</MenuItem>
                        <MenuItem value="select">Dropdown</MenuItem>
                        <MenuItem value="checkbox">Checkboxes</MenuItem>
                        <MenuItem value="multichoices">Multiple choices</MenuItem>
                    </Select>
                </FormControl>

                <br /><br />

                {

                    ['select', 'checkbox', 'multichoices'].includes(question.type) ?
                        <FormControl fullWidth variant="outlined">
                            <br />
                            <FormLabel>Options</FormLabel>
                            <br />

                            {
                                (question.options || []).map((option, index) => {
                                    return (
                                        <div
                                            style={{ position: 'relative', marginBottom: '10px' }}
                                            key={index}
                                        >
                                            {icon}

                                            <TextField
                                                sx={{ width: '80%', marginRight: '10px' }}
                                                placeholder='Enter option text'
                                                value={option}
                                                autoFocus={true}
                                                onChange={(e) => onChangeOption(question, index, e.target.value)}
                                            />

                                            <CancelIcon
                                                onClick={onDeleteOption.bind(this, question, index)}
                                            />
                                        </div>
                                    )
                                })
                            }

                            <div style={{
                                    float: 'right',
                                    border: '1px solid #e0e0e0',
                                    width: '80%',
                                    background: '#f9f9f9',
                                    color: '#969799',
                                    padding: '5px 10px',
                                    cursor: 'text',
                                    ...(icon ? { marginLeft: '26px' } : {})
                                }}
                                onClick={addOption.bind(this, question)}
                            >

                                Add option
                            </div>
                        </FormControl>
                        : null
                }
            </div>
        )
    }

    return (
        <Dialog onClose={onClose} aria-labelledby="modal-title" open={open} fullScreen className='candidate-detail' sx={{ width: '40%', left: 'inherit' }}>
            <DialogTitle id="modal-title">
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1} >{ customQuestion ? customQuestion.name : 'New custom question' }</Box>
                    <Box display="flex" alignItems="center">
                        <Box>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>

                            <Button variant="contained" color="success" onClick={onSave} disabled={saving}>
                                Save
                            </Button>

                            {
                                customQuestion ?
                                    (
                                        <Button variant="contained" color="error" onClick={onDelete} disabled={saving} style={{ marginLeft: '5px' }}>
                                            Delete
                                        </Button>
                                    ) : null
                            }
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent>
                <FormControl fullWidth variant="outlined" required>
                    <FormLabel>Name</FormLabel>
                    <TextField value={name} onChange={(e) => setName(e.target.value)} />
                </FormControl>

                <br />
                <br />

                {questions.map((question, index) => renderQuestion(question, index))}

                <Button
                    onClick={addQuestion}
                    variant="contained"
                    color='info'
                >
                    + Add question
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default CustomQuestionForm;
